import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import { SupabaseProvider } from './lib/supabase';

// Global error tracking
let hasReportedError = false;
const reportError = (error: Error) => {
  // Only report the first error to prevent spamming
  if (!hasReportedError) {
    console.error('Application error:', error);
    hasReportedError = true;
  }
};

// Set up global error boundaries
window.addEventListener('error', (event) => {
  reportError(event.error);
  
  // Add visible UI indicator for errors in deployed environments
  if (import.meta.env.PROD) {
    const errorElement = document.createElement('div');
    errorElement.style.position = 'fixed';
    errorElement.style.bottom = '10px';
    errorElement.style.right = '10px';
    errorElement.style.background = 'rgba(220, 38, 38, 0.9)';
    errorElement.style.color = 'white';
    errorElement.style.padding = '10px 16px';
    errorElement.style.borderRadius = '4px';
    errorElement.style.zIndex = '9999';
    errorElement.style.font = '14px sans-serif';
    errorElement.textContent = 'Une erreur s\'est produite. Essayez de rafraîchir la page.';
    
    // Add refresh button
    const refreshButton = document.createElement('button');
    refreshButton.textContent = 'Rafraîchir';
    refreshButton.style.marginLeft = '10px';
    refreshButton.style.padding = '4px 8px';
    refreshButton.style.background = 'white';
    refreshButton.style.color = 'black';
    refreshButton.style.border = 'none';
    refreshButton.style.borderRadius = '4px';
    refreshButton.style.cursor = 'pointer';
    refreshButton.addEventListener('click', () => {
      window.location.reload();
    });
    
    errorElement.appendChild(refreshButton);
    document.body.appendChild(errorElement);
  }
});

window.addEventListener('unhandledrejection', (event) => {
  reportError(event.reason);
});

// Add recovery mechanism for auth issues in production
if (import.meta.env.PROD) {
  // Check if the application appears to be stuck (for deployed environments)
  let lastActivityTimestamp = Date.now();
  const ACTIVITY_TIMEOUT = 10000; // 10 seconds
  
  // Track activity
  ['click', 'keydown', 'mousemove'].forEach(eventType => {
    window.addEventListener(eventType, () => {
      lastActivityTimestamp = Date.now();
    });
  });
  
  // Set up a periodic check
  setInterval(() => {
    // Only run recovery checks if the user is active
    const hasRecentActivity = (Date.now() - lastActivityTimestamp) < ACTIVITY_TIMEOUT;
    
    if (hasRecentActivity) {
      const loadingElement = document.querySelector('.loading-indicator');
      const isLoading = loadingElement !== null;
      
      // Check if we're stuck in a loading state for too long
      const loadingStartTime = sessionStorage.getItem('loadingStartTime');
      const now = Date.now();
      
      if (isLoading && loadingStartTime) {
        const loadingDuration = now - parseInt(loadingStartTime, 10);
        
        // If loading for more than 15 seconds, try recovery
        if (loadingDuration > 15000) {
          console.log('Loading timeout detected, attempting recovery...');
          
          try {
            // Check if we have valid auth data
            const authToken = localStorage.getItem('sb-supabase-auth-token');
            const userRole = sessionStorage.getItem('userRole');
            
            if (authToken && userRole) {
              console.log('Found valid auth data, redirecting to appropriate dashboard');
              
              // Redirect to the appropriate dashboard based on role
              if (userRole === 'admin') {
                window.location.href = '/admin';
              } else if (userRole === 'formateur') {
                window.location.href = '/trainer';
              } else if (userRole === 'etudiant') {
                window.location.href = '/student';
              } else {
                // Generic fallback
                window.location.href = '/dashboard';
              }
            } else if (authToken) {
              // We have auth but no role - go to generic dashboard
              window.location.href = '/dashboard';
            } else {
              // No auth - go to login
              window.location.href = '/login?recovery=1';
            }
          } catch (e) {
            console.error('Error during recovery:', e);
            // Last resort - force reload
            window.location.reload();
          }
        }
      } else if (isLoading) {
        // Start tracking loading time
        sessionStorage.setItem('loadingStartTime', now.toString());
      }
    }
  }, 5000);
}

// Fallback UI if the app fails to render
const renderFallbackUI = () => {
  document.body.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      text-align: center;
      font-family: sans-serif;
      color: #4b5563;
      padding: 0 20px;
    ">
      <h1 style="font-size: 2rem; margin-bottom: 1rem; color: #1e40af;">FormaNote by FormaVerse</h1>
      <h2 style="font-size: 1.5rem; margin-bottom: 1rem;">Une erreur est survenue</h2>
      <p style="margin-bottom: 2rem;">L'application n'a pas pu être chargée correctement. Veuillez essayer de:</p>
      <ul style="text-align: left; margin-bottom: 2rem;">
        <li style="margin-bottom: 0.5rem;">Rafraîchir la page</li>
        <li style="margin-bottom: 0.5rem;">Vider le cache de votre navigateur</li>
        <li style="margin-bottom: 0.5rem;">Essayer un autre navigateur</li>
        <li>Revenir à <a href="/" style="color: #2563eb; text-decoration: underline;">la page d'accueil</a></li>
      </ul>
      <button 
        onclick="window.location.reload()" 
        style="
          background-color: #2563eb;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          font-size: 1rem;
          cursor: pointer;
        "
      >
        Rafraîchir la page
      </button>
    </div>
  `;
};

try {
  const rootElement = document.getElementById('root');
  
  if (!rootElement) {
    throw new Error('Root element not found');
  }
  
  // Use simple BrowserRouter for better compatibility
  createRoot(rootElement).render(
    <StrictMode>
      <BrowserRouter>
        <SupabaseProvider>
          <App />
        </SupabaseProvider>
      </BrowserRouter>
    </StrictMode>
  );
  
  // Add navigation persistence to help with page refreshes
  window.addEventListener('beforeunload', () => {
    // Store the current path to help with navigation after refresh
    if (window.location.pathname !== '/') {
      sessionStorage.setItem('lastPath', window.location.pathname + window.location.search);
    }
  });
  
  // Check for navigation after refresh
  const lastPath = sessionStorage.getItem('lastPath');
  if (lastPath && window.location.pathname === '/') {
    // Clean up
    sessionStorage.removeItem('lastPath');
    // Use history API to avoid refreshing the page
    window.history.replaceState(null, '', lastPath);
  }
  
  // Add page visibility handling to improve state management
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Refresh auth state when page becomes visible again
      console.log('Page visible, refreshing state');
      // The actual refresh happens via the auth listeners
    }
  });
  
} catch (error) {
  reportError(error as Error);
  renderFallbackUI();
}